/**
 * Shared client/server request schema: the field list, equipment/payer enums
 * and the one validation function both the Preact form (future task) and
 * request-routes.ts import. Pure and side-effect-free (no pg, no node:http)
 * so it is safe to bundle into the client build.
 *
 * CRITICAL (carried forward from Task 2 review): the pg-mem test fixture
 * (test/helpers/pgmem.ts) omits the real `one_off_xor_pattern` and
 * `destination_id_xor_text` CHECK constraints, so validateRequestInput's XOR
 * checks below are the ONLY guard exercised by the unit-test suite.
 * Real-Postgres CHECK enforcement is asserted separately
 * (test/core/migration-ddl.spec.ts's realPg block, Task 9 turns those suites
 * on). Keep both XOR branches below airtight.
 */

// Safe to import here, and checked rather than assumed: `./eircode.js` imports
// NOTHING, so it keeps this module pure and client-bundlable as the docblock
// above promises.
import { formatDestination, parseDestinationEircode } from './eircode.js';
// Board 2638: pure as well (it reads only `Intl`), so the promise above holds.
import { dublinInstant, dublinWallClock } from './dublin-time.js';

// ⛔⛔ THIS IS THE WRITE ALLOW-LIST, NOT THE FORM'S OPTION LIST, AND THE TWO ARE
// DELIBERATELY DIFFERENT SINCE T2174. `validateRequestInput` below filters every
// submitted `equipment` value against THIS tuple and refuses anything outside it,
// so every value ever stored is one of these four.
// ⛔ `oxygen` is still admitted here although T2174 removed it from the FORM.
// That is a live ruling with a recorded acceptance, not an oversight — the
// reasoning, the evidence and the accepted shortfall (this API still accepts
// `oxygen` from a hand-crafted POST) live in `docs/decisions-scope.md` under
// `## Decisions locked`, which is the record a governance reader reads.
// ⚠️ DO NOT restate that reasoning here. It was written out in four places at
// T2174 and a false premise in it survived the first correction pass precisely
// because there were four copies to keep in step.
export const EQUIPMENT_OPTIONS = ['wheelchair', 'walker', 'oxygen', 'escort seat'] as const;
export type EquipmentOption = (typeof EQUIPMENT_OPTIONS)[number];

// ⭐⭐ T2174 (Alex, 2026-09-17) — WHAT THE NURSE IS OFFERED, which is a STRICT
// SUBSET of the allow-list above. Alex dropped `oxygen` from the form: the nurse
// is telling ECMB how to prep the bus, and oxygen was rendered to nobody (the
// office alert cannot express equipment, the nurse projection drops it, and the
// kiosk seam carries only the derived wheelchair bit), so collecting it was
// Article 9 data with no reader. The ORDER here is the rendered order and is
// Alex's: Wheelchair access, Carer seat, Mobility aids.
// ⛔ NEVER make this the validator's source. The subset relation is the whole
// design — widen the form by adding here, and retire a stored key by removing it
// from EQUIPMENT_OPTIONS only once no row holds it.
// ⭐ `satisfies` makes MEMBERSHIP a COMPILE-TIME guarantee: a typo here
// (`'escort seet'`) is a type error, where before it was caught only by the
// runtime arm in `test/server/request-routes.spec.ts`. The literal tuple and its
// order survive `satisfies`, which matters because the order IS Alex's ruling —
// so this cannot be derived by filtering `EQUIPMENT_OPTIONS`. Strict-subset and
// no-duplicates remain the runtime arm's job.
export const EQUIPMENT_FORM_OPTIONS = [
  'wheelchair',
  'escort seat',
  'walker',
] as const satisfies readonly EquipmentOption[];
export type EquipmentFormOption = (typeof EQUIPMENT_FORM_OPTIONS)[number];

export const PAYER_KINDS = ['hse', 'house', 'self'] as const;
export type PayerKind = (typeof PAYER_KINDS)[number];

/**
 * The payer kinds offered for a destination, BY ITS CLASS (board 2186, Alex 2026-09-18).
 *
 * ⛔ A PRICE-BOOK DESTINATION MAY NOT BE PAID BY THE PASSENGER. `portal.destinations` is
 * account-scoped and the kiosk projects only that account's PRICED destinations into it
 * (`core/destinations-repo.ts:12-16`), so "on the curated list" IS "on the price book" — the
 * portal cannot read `account_prices` itself (ADR1-D36(b)) and does not need to.
 *
 * ⭐ board 2814 M4 (Alex's ruling of 2026-10-06, ADR1-D17 as amended): THE OLD REASON HERE
 * WENT FALSE AND WAS REWRITTEN. It said the kiosk refuses every portal `self` row and the
 * crossing answers 409 `crossing_payer_self` with no booking. From the kiosk's v46 a cash
 * request CROSSES: `inquiries_portal_billing` admits `self` (still with an account), and the
 * booking lands on the home's account with `payer_kind = 'self'`, which every kiosk money
 * door refuses and the account invoice excludes. The narrowing STANDS on the rule it always
 * named: a price-book destination is paid by the home, never by the passenger, so Cash is
 * offered only on the free-typed branch.
 *
 * ⚠️ ONE-DIRECTIONAL, deliberately. A `destinationId` is server-provably on-list; free text is
 * NOT provably off-list — a nurse may type the name of a priced place and reach all three. That
 * is acceptable: a cash request for a typed destination crosses as cash, and Helen sees the
 * payer in words before she confirms ("Change who pays" on the booking corrects a mistake). No
 * new payer token is introduced: `self` stays `self`, and only its LABEL differs
 * (`client/copy.ts`).
 */
export function allowedPayerKinds(isFreeTyped: boolean): readonly PayerKind[] {
  return isFreeTyped ? PAYER_KINDS : (['hse', 'house'] as const);
}

// The five portal.request_status enum values, in DB-declaration order. Not
// part of the input schema (status is server-assigned/transitioned), but
// shared here so both request-routes.ts and requests-repo.ts import one
// definition instead of two copies drifting apart.
export const REQUEST_STATUSES = ['new', 'priced', 'confirmed', 'declined', 'cancelled'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

/**
 * Recurring-run vocabulary (extended 2026-07-21, review P5). One shape, not
 * a union: stored rows from v1 ({frequency:'weekly'|'fortnightly', weekday,
 * time}) stay valid unchanged.
 *
 * - weekly | fortnightly:  weekday + time (the v1 shape)
 * - every_n_weeks:         intervalWeeks (2-12) + weekday + time
 * - monthly_date:          dayOfMonth (1-31) + time; when a month lacks the
 *                          date (the 31st in June), the run clamps to the
 *                          month's last day — enforced when occurrences are
 *                          built (the FORK), documented here so the rule has
 *                          one home.
 *
 * Truly irregular runs stay one-off requests; a pattern that changes every
 * time is not a pattern (Vlad's P5 ruling: offer the realistic shapes, not
 * a calendar engine before occurrences exist).
 */
/**
 * What the time on a request actually refers to (T632).
 *
 * One `Time` control used to serve both journey kinds, so "10:00" was
 * unreadable: a dialysis run means "be at the hospital by 10:00" and a
 * day-centre run means "collect from the home at 10:00". Helen derives the
 * other end herself -- she already does under manual dispatch -- but she can
 * only do that if she is told which one the nurse stated.
 *
 * Deliberately NOT solved with a single label. Neither name is true for both
 * kinds: not every run has an appointment (seed.ts seeds a day centre, and
 * migration v3's own comment records that journeys are more often FROM a
 * health-care centre than to one), so `Appointment time` would be a lie on
 * those rows, and `Pickup time` asks the nurse to compute a travel time she
 * cannot know.
 */
export type TimeMeaning = 'appointment' | 'collection';

export const TIME_MEANINGS: readonly TimeMeaning[] = ['appointment', 'collection'];

/**
 * ⭐⭐ T2088 M2 (ADR-0006, plan D1/D2) — THE NAMED STATE OF A TICKED RETURN'S
 * TIME, as ONE closed vocabulary. Imported by the validator below, by
 * `core/requests-repo.ts` (the write and both list reads), by both portal
 * projections and, from M4 and M7, by both UIs, so a third state fails at
 * COMPILE time in every switch rather than defaulting silently. Modelled on
 * `TIME_MEANINGS` above; the database half is v14's
 * `requests_return_at_state_known` CHECK over the same two words.
 *
 * `'unset'` is the true reading of THREE different rows: the instant is
 * present; there is no return leg at all; the row predates v14. `'to-confirm'`
 * means ONE thing and only one: the nurse said the return time is not yet
 * known. ⛔ ONLY `'to-confirm'` IS EVER ON THE WIRE (`NewRequest.returnAtState`);
 * `'unset'` is the column default and a READ value, never something a caller
 * sends, because an omitted marker is the refused state and not a default.
 */
export const RETURN_AT_STATES = ['unset', 'to-confirm'] as const;
export type ReturnAtState = (typeof RETURN_AT_STATES)[number];

/**
 * Board 2788 (plan DD-7, DD-8): the home's ask on a Confirmed request, ONE slot
 * on the row. The database half is migration v17's CHECKs over the same words
 * (`requests_ask_kind_known`, `requests_ask_state_known`,
 * `requests_ask_decline_reason_known`); `core/requests-repo.ts` writes them and
 * the projections (M3, M4) read them, so a new word fails at compile time in
 * every switch. TEXT + CHECK, never an enum (v4's ruling).
 *   ask kind  : `change` (a new day and time) or `cancel`.
 *   ask state : `none` (never asked), `open`, `accepted`, `declined`, `closed`
 *               (superseded by Helen's own change or by a cancel ask, or the
 *               request ended).
 *   decline   : DD-5's closed list of Helen's reasons. A code, never free text,
 *               and it never reaches an email or a kiosk event (S8).
 */
export const ASK_KINDS = ['change', 'cancel'] as const;
export type AskKind = (typeof ASK_KINDS)[number];
export const ASK_STATES = ['none', 'open', 'accepted', 'declined', 'closed'] as const;
export type AskState = (typeof ASK_STATES)[number];
export const DECLINE_REASONS = ['no_bus', 'too_close', 'please_ring'] as const;
export type DeclineReason = (typeof DECLINE_REASONS)[number];

/**
 * Board 2788 (plan DD-13): a home's change or change ask is refused once this
 * many of them have landed on one request (`portal.requests.home_writes`). It
 * bounds the office emails one request can cause. Helen's writes and the cancel
 * ask do not count. Exported so the route and the card can say why.
 */
export const HOME_WRITES_CAP = 10;

export type ScheduleFrequency = 'weekly' | 'fortnightly' | 'every_n_weeks' | 'monthly_date';

export type SchedulePattern = {
  frequency: ScheduleFrequency;
  /** Required for weekly/fortnightly/every_n_weeks; absent for monthly_date. */
  weekday?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** every_n_weeks only: 2-12. */
  intervalWeeks?: number;
  /** monthly_date only: 1-31. */
  dayOfMonth?: number;
  time: string;
};

export type NewRequest = {
  residentId: number;
  /**
   * ⭐⭐ T2075 M2 (plan D3, S6) — WHO ASKED FOR THIS JOURNEY: a `portal.staff`
   * id the caller picked. The server re-checks it against the session's home,
   * `active`, and (for a `group` account) the session's own group before it is
   * written, and copies the GROUP SNAPSHOT from the staff row — never from the
   * body. A body `requestedByGroupLabel`, `nurseId`, `homeId`, `enteredBy` or
   * `groupLabel` is not a field this type knows and is dropped by the
   * rebuild-by-name below.
   *
   * ⛔ REQUIRED SINCE T2075 M3 (plan Step 2, R-3): the form's picker is its
   * first control and a request cannot be sent without a name, in the browser
   * and on the wire alike. The validator's REQUIRED arm is the `residentId`
   * shape; the one message is `'Choose who is requesting this trip.'`, which
   * `client/copy.ts` `requesterRequired` records and the form spec pins equal.
   * Every pre-v13 row keeps its NULL: the requirement is on what is WRITTEN
   * from here on, never a backfill.
   */
  requestedByStaffId: number;
  room: string;
  equipment: string[];
  /** Exactly one of destinationId / destinationText is set (P5: a nurse may
   *  type an address/Eircode instead of picking from the curated list). */
  destinationId: number | null;
  destinationText: string | null;
  /**
   * ⭐ T1683 M7 (A4) — THE NURSE SAYS THE JOB COMES BACK.
   *
   * ⭐⭐ THE RULING IS REVERSED AND THE TIME IS COMING. Alex, 2026-09-02, via
   * `/scope` of the QA report (question 708 in the lane store), reversed board
   * #1681's deferral; boards #1681 and #1683 M7 record the reversal. T1717 M5
   * adds `returnAt` to this type as an ISO instant on a ONE-OFF request -
   * required when `returnTrip` is ticked there - and T1717 M6 carries it across
   * the seam onto the kiosk's `bookings.return_at`.
   * ⛔ THE FLAG BELOW STILL RECORDS ONLY **THAT** A RETURN IS WANTED. `returnAt`
   * is the field that records WHEN, and the two travel together: every render
   * of the instant is gated on this boolean, so a flip to one-way hides both.
   * ⭐ THE TWO FACTS THIS BLOCK USED TO CARRY AS COUNTER-EXAMPLES ARE NOW THE
   * CONVERGENCE PATH, not exceptions. The kiosk's `bookings.return_at
   * TIMESTAMPTZ` (its `core/migrations.ts` v17) was noted here as "zero writers,
   * zero readers"; M6 makes it the DESTINATION column rather than adding one.
   * The public SITE form's free-text `returnTime` in `inquiries.extra` stays
   * free text on its own lane - the asymmetry (site: free text, display-only;
   * portal: a validated instant reaching the booking) is deliberate and carries
   * a P2 follow-up to normalise the site's into `return_at`.
   *
   * ⛔ IT IS ONE JOURNEY, NEVER TWO REQUESTS. The kiosk prices ONE booking at
   * twice the account entry (`PortalRequests.tsx`, following `Quote.tsx`
   * verbatim); it does not create a second row on either side of the seam.
   *
   * ⚠️ ABSENT MEANS `false`, NOT AN ERROR — and that is back-compatibility with
   * a live surface rather than leniency. `POST /api/requests` has callers that
   * predate this field (the e2e portal journeys among them), and every request
   * already written genuinely IS a one-way job, which is why the column is
   * `NOT NULL DEFAULT false`. A non-boolean is still refused below: silence is a
   * true answer, a wrong TYPE is not.
   */
  returnTrip: boolean;
  /**
   * ⭐⭐ T1717 M5 (DD4, P0-7) — **WHEN** the bus brings them back, and the field
   * that spends the reversal the block above announces.
   *
   * ⛔ ONE INSTANT, NOT A DATE AND A TIME. Since board 2638 the VALIDATOR
   * composes it (`dublinInstant`, the journey's Irish day plus the typed
   * `returnTime`), the same function the outbound leg uses, so both legs are
   * Dublin time whatever the device says; the `returnAt > journeyDate` test is
   * an INSTANT comparison, not a string one, and schema v11's
   * `requests_return_after_outbound` CHECK backs it. ⛔ The wire never carries
   * this key: a body that does is a stale page and is refused (`STALE_PAGE_MESSAGE`).
   *
   * ⛔ ONE-OFF ONLY, AND THAT RULE IS THE VALIDATOR'S ALONE. A recurring request
   * keeps the tick with NO instant: `one_off_xor_pattern` makes `journey_date`
   * NULL there, which makes the CHECK's `journey_date IS NULL` arm pass
   * unconditionally — the database cannot express "one-off only" and does not
   * claim to (the column comment says so too). A return time for a weekly run is
   * a named residual; the pattern would carry its own `returnTime` key.
   *
   * ⛔⛔ THIS PARAGRAPH'S RULING IS REVERSED (T2088 M2, ADR-0006; Alex's Q155 of
   * 2026-09-16), AND THE OLD TEXT IS QUOTED SO A READER CAN SEE WHAT MOVED. It
   * said: "NULL IS 'NO RETURN LEG', NEVER 'UNKNOWN' — every request written
   * before M5 and every one-way job for ever. Required when `returnTrip` is
   * ticked on a one-off request and refused outright when it is not, so the
   * boolean stays the single gate every render is keyed on and a flip to
   * one-way hides both." NULL now means EITHER no return leg OR a return whose
   * time is still to be confirmed, and `returnAtState` below is the
   * DISCRIMINATOR: `returnAt === null` alone is never the test again (v14's
   * superseding column comment says the same at the catalogue). What survives
   * of the old sentence: still refused outright when `returnTrip` is not
   * ticked, so the boolean stays the single gate every render is keyed on; and
   * still REQUIRED on a ticked one-off return UNLESS the request SAYS the time
   * is to be confirmed. An omitted `returnAtState` is the refused state, not a
   * default.
   */
  returnAt: string | null;
  /**
   * ⭐⭐ T2088 M2 (ADR-0006, plan D2) — "THE RETURN TIME IS STILL TO BE
   * CONFIRMED", SAID OUT LOUD. Present as exactly `'to-confirm'` when the nurse
   * ticked Return and said the time is not yet known; ABSENT otherwise. It is
   * drawn from `RETURN_AT_STATES` so the vocabulary has one owner, but ONLY the
   * `'to-confirm'` member is legal here: `'unset'` is a column default and a
   * read value, never a wire value, because an omitted key is the refused
   * state (the two findings kept in the validator's docblock are why) and the
   * only thing a caller can usefully SAY is the one word that means something.
   * ⛔ IT TRAVELS WITH THE FLAG AND WITHOUT THE INSTANT: `'to-confirm'` beside
   * a `returnAt`, or on a job that does not come back, is refused by the
   * validator and, on real PostgreSQL, by v14's
   * `requests_to_confirm_needs_timeless_return` CHECK. The repo writes it when
   * present and lets the column default to `'unset'` when it is not.
   */
  returnAtState?: Extract<ReturnAtState, 'to-confirm'>;
  payerKind: PayerKind;
  /**
   * ⛔⛔ THE NEXT-OF-KIN PAIR WAS HERE AND IS GONE (T1868 M6). It is not
   * "optional now" and it is not "unused" — the two keys are absent from this
   * type, from the validator, from every write path and from `portal.requests`'
   * write list, so a caller that still POSTs them writes nothing anywhere.
   *
   * ⛔ WHAT REPLACED IT IS NOT A FIELD ON THIS TYPE. LCC asked for next of kin
   * off the request; the number Helen rings about a job is now the HOME's own
   * `portal.homes.fallback_phone`, one organisation-level value the kiosk
   * operator sets, resolved home-side at render. That is a deliberate change of
   * CARDINALITY as well as of ownership: one number per home instead of one
   * third party's name and number per journey.
   *
   * ⚠️ THE COLUMNS STILL EXIST on `portal.requests` and `portal.residents` and
   * are NOT dropped by any migration — `requests.next_of_kin_phone` is v1 NOT
   * NULL, so the write path stores `''` and the other three take NULL. The
   * standing roster copies are nulled out of band by
   * `docs/runbooks/portal-v12-nok-backfill.sql`, because `purgeExpired` never
   * touches `portal.residents` and the retention period for them is unruled.
   *
   * ⭐ Board 2638: on the VALUE this is the canonical ISO instant; on the WIRE
   * the key carries the Irish DAY (`YYYY-MM-DD`) and `journeyTime` the Irish
   * `HH:MM`, and the validator composes the instant in `Europe/Dublin`.
   */
  journeyDate: string | null;
  schedulePattern: SchedulePattern | null;
  /** What the stated time refers to (T632). Null means the nurse was not
   *  asked or did not say -- every row written before migration v4, and any
   *  one-off request today, where the marker is captured but not required
   *  (making it required on the one-off branch reds a Playwright journey
   *  owned by another lane; see docs in portal/docs/t632-time-field-notes.md). */
  timeMeaning: TimeMeaning | null;
  /**
   * ⭐⭐ T1868 M5 (P0-4) — **WHEN THE BUS COLLECTS HER**, on the arm where the
   * time she stated is the APPOINTMENT and not the pickup.
   *
   * ⛔⛔ THE SERVER DERIVES IT (board 2638, plan D1), which REVERSES the ruling
   * this paragraph used to state: "IT IS NURSE-STATED, NOT DERIVED ... THE
   * SERVER NEVER RE-DERIVES IT", a value the client proposed and this validator
   * only bounded. There has been no pickup control since 2026-09-13 (board
   * 2073), so there was no edit left to protect, and the client-supplied value
   * was the gap security review M1 (board 2188) named. It is now
   * `pickupInstantFor(appointment, opts.pickupOffsetMinutes)`, the offset read
   * from the SESSION home's `pickup_offset_minutes` by the route. (Migration
   * v16 supersedes v12's "nurse-stated" column comment, board 2707 M2.)
   *
   * ⛔ THE BOUNDS STAY, AS GUARDS OVER THE DERIVATION, AND ONE IS A BOUND
   * `returnAt` DOES NOT NEED (the strict-ISO gate went with the wire value;
   * `MAX_PICKUP_OFFSET_MINUTES` is the fourth): strictly BEFORE the appointment
   * (v12's `requests_pickup_before_appointment` refuses an inverted row, so
   * without this the failure is a bare 500); inside the SAME `MAX_ADVANCE_DAYS`
   * window as its two siblings, one named constant because two windows that can
   * drift apart is how one of them stops being enforced; and **not in the
   * past**. That last one is not an extra feature: `returnAt` needs no past
   * bound because `returnAt > journeyDate > now` implies it, whereas
   * `pickupAt < journeyDate` implies nothing of the kind — an offset of up to
   * 480 minutes against an appointment that need only be in the FUTURE derives a
   * pickup that has already happened. The implication is missing, so the bound
   * is written.
   *
   * ⛔ NEVER DERIVED ON THE `collection` ARM. v3's ruling is that a `collection`
   * time IS the pickup, so a second pickup beside it would be two answers to one
   * question — and v12's comment rules the column NULL "for ever" on that arm.
   * A body that carries this key at all is a stale page and is refused.
   *
   * ⛔ NULL IS "NO PICKUP STATED" AND IT IS PERMANENT FOR EVERY PRE-v12 ROW.
   * There is no backfill; every render must omit the phrase rather than print a
   * blank label.
   */
  pickupAt: string | null;
  /**
   * ⭐⭐ Board 2647 (P4) — THE OFFSET `pickupAt` WAS DERIVED WITH, in minutes;
   * null exactly when `pickupAt` is. The validator sets it from the same
   * `opts.pickupOffsetMinutes` it passed to `pickupInstantFor`, and
   * `insertRequest` writes it to `pickup_offset_minutes_used` as given. Until
   * 2647 the repo re-read the home's offset in a second query, which could
   * differ from the one the pickup was derived with if an operator changed it
   * between the two reads. With one input the two columns agree by
   * construction (`journey_date - offset = pickup_at`) on every row written
   * from 2647 on; a mismatch on an older row is a fact about the older write
   * path.
   * ⛔ REQUIRED, not optional, so a second caller that builds a `NewRequest`
   * cannot forget it and silently store NULL beside a pickup.
   * ⭐ Migration v16 (board 2707 M2) re-takes both column comments
   * (`pickup_at`, `pickup_offset_minutes_used`) for this wire; v12's text stays
   * as written and is superseded in the catalogue.
   */
  pickupOffsetMinutesUsed: number | null;
  notes: string;
};

export type ValidateRequestOptions = {
  /** ⚠️ `syntheticPhones` STOOD HERE (T1868 M6). It decided ONE thing — whether a
   *  next-of-kin number had to sit in the synthetic drama range — and there is
   *  no phone on a request any more, so an option that decided nothing was
   *  removed rather than left as a parameter every caller must still supply.
   *  ⛔ `PORTAL_MODE` IS NOT AFFECTED: `serve.ts` and `notify/email.ts` read it
   *  for the DELIVERY POSTURE, its boot guard is unchanged, and this validator
   *  never read the variable itself.
   *  Caller-supplied clock, so "journeyDate must be in the future" stays
   *  deterministic and time-injectable under tests (matches the db.ts /
   *  auth-repo.ts `now: Date` convention used everywhere else in the repo).
   *  ⭐ Board 2638: the server passes `deps.now()`; the browser passes its
   *  estimate of the SERVER's clock (`RequestForm.tsx`), never the device's. */
  now: Date;
  /** ⭐ Board 2638 (plan D1) — the session home's `pickup_offset_minutes`, from
   *  which the appointment arm DERIVES the collection. Null or absent (or not a
   *  positive number) derives nothing, and the appointment arm then refuses. */
  pickupOffsetMinutes?: number | null;
  /** ⭐ Board 2638 (plan D3) — apply only the two LOWER bounds (journey in the
   *  future; collection not already past) and skip the three UPPER ones (journey,
   *  collection and return within `MAX_ADVANCE_DAYS`). The browser sets it: its
   *  clock estimate can lag the server's but never run ahead, so a lower bound
   *  can only get more lenient there and an upper one could wrongly refuse.
   *  ⛔ Defaults to false, so a server caller that omits it applies every bound;
   *  the server re-checks all five on every send. */
  lowerBoundsOnly?: boolean;
};

/**
 * ⛔⛔ Board 2638 (plan D2) — WHAT A PAGE LOADED BEFORE THE DEPLOY IS TOLD. Its
 * bundle composes instants in the device's zone and sends `journeyDate` as an
 * ISO instant with `pickupAt` / `returnAt` beside it; that body is refused
 * FIRST and ALONE, never trusted and never reinterpreted. "Close and open"
 * rather than "reload": the portal installs standalone, with no reload button,
 * and a reopen fetches the new bundle (`cache-control: no-store`, no service
 * worker). Keyed `journey`, which the old bundle renders and focuses under the
 * date.
 */
export const STALE_PAGE_MESSAGE =
  'This page is out of date. Close the portal and open it again, then send the request again, or ring East Coast Minibus.';
/** The same refusal on `POST /api/requests/:id/return-time`, keyed `returnAt`
 *  because that is the one key the old My requests bundle renders. */
export const STALE_RETURN_TIME_MESSAGE =
  'This page is out of date. Close the portal and open it again, then set the return time again, or ring East Coast Minibus.';

export type ValidateRequestResult =
  | { ok: true; value: NewRequest }
  | { ok: false; errors: Record<string, string> };

// ⛔⛔ THE NEXT-OF-KIN PHONE RULE STOOD HERE AND IS DELETED (T1868 M6):
// `SYNTHETIC_PHONE_RE`, `SYNTHETIC_TEST_NUMBERS`, `SYNTHETIC_PHONE_EXAMPLE`,
// `LAX_E164_RE` and `nextOfKinPhoneProblem`. It was the ONE rule that decided a
// phone number on this service, it had exactly two callers — the request
// validator below and the roster's `validateResidentFields` — and M6 removes
// next of kin from both. Deleting it is the same act as removing the field; a
// exported rule with no caller is the "shape with no reader" pattern this repo
// refuses by name.
//
// ⛔ IT IS **NOT** THE RULE FOR `portal.homes.fallback_phone`, and that is a
// decision made at M6 rather than an omission. The fallback phone deliberately
// has NO charset rule (`opsUpdateHomeSettings` in `server/request-routes.ts`
// carries the reasoning): a digits-and-plus rule would refuse exactly the
// values that read as ORGANISATIONAL — "Reception: 01 555 0100", a day line and
// a night line, an extension — while admitting a bare personal mobile
// unchanged. Its refusals correlate NEGATIVELY with the hazard it would appear
// to address, so adopting this rule there would have bought rigour-shaped
// nothing. The bound that IS enforced there is the storage cap plus
// `cleanStoredText`.

/**
 * Shared text cleaner for third-party-supplied strings that will be STORED.
 *
 * ⛔ MOVED HERE FROM `server/request-routes.ts` — T1717 M4, and moved rather
 * than copied: two copies of a cleaner is two to keep in step, and the failure
 * is invisible — a NUL one surface refuses is stored by the other.
 * ⚠️ THE FIELD THAT FORCED THE MOVE IS GONE (T1868 M6 removed next of kin) AND
 * THE FUNCTION STAYS SHARED. Its server callers are the ops actor header, the
 * nurse display name, the two roster fields and the home's `fallback_phone`;
 * `sameStoredName` below composes with it; and it is pure — no pg, no
 * node:http — so it remains safe in the client bundle.
 *
 * Strips C0/C1 control characters (including NUL), drops bidi overrides,
 * collapses whitespace, and returns null when nothing VISIBLE survives.
 * Callers apply their own length limit.
 *
 * ⛔ Used for BOTH the actor header and the nurse display name, deliberately.
 * They arrive by different routes but land in the same places -- an audit
 * trail and a CSV emailed to a home's administrator -- and the failure modes
 * are identical: a NUL is rejected by real Postgres as 22021 and would surface
 * as a bare 500, and a bidi override makes a stored name RENDER as somebody
 * else's in the sheet used to decide who keeps access.
 */
export function cleanStoredText(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  // C0 (\u0000-\u001F, which is where NUL lives), DEL (\u007F) and C1
  // (\u0080-\u009F). Written as explicit escapes, never as literal control
  // characters in the source -- a literal NUL in a regex is invisible in a
  // diff, and this repo has a recorded incident of exactly that shape.
  // eslint-disable-next-line no-control-regex
  const stripped = raw.replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ');

  // ⛔ BIDI CONTROLS ARE DROPPED OUTRIGHT, not merely counted as invisible.
  // U+202E and friends REORDER what follows, so a value can be made to render
  // as somebody else's name in the very records meant to name the right person.
  //
  // ⛔⛔ THE CLASS IS THE UNICODE PROPERTY, NOT A HAND-WRITTEN RANGE, and that
  // is a correction rather than a tidy-up. The hand-written
  // `[\u202A-\u202E\u2066-\u2069]` covered the EMBEDDING/OVERRIDE and ISOLATE
  // families and missed the three STANDALONE marks -- U+061C ARABIC LETTER MARK,
  // U+200E LEFT-TO-RIGHT MARK and U+200F RIGHT-TO-LEFT MARK -- which reorder a
  // run just as effectively and need no terminator to do it. MEASURED
  // (2026-09-08, board 1868 / 1873): a U+200F placed before `01 555 0100`
  // survived every caller of this function, landed in
  // `portal.homes.fallback_phone`, and rendered REVERSED on the screen Helen
  // rings from -- a wrong number presented as a right one.
  // ⚠️ `\p{Bidi_Control}` is exactly those twelve code points and nothing else,
  // verified on the pinned node v24.18.1 by enumerating the whole code space;
  // `test/server/request-routes.spec.ts` RE-DERIVES that set from the property
  // rather than trusting this comment, and pins the three marks by name.
  // Naming the property is also what stops the class rotting the way the range
  // did: a future Unicode addition arrives without an edit here.
  const noBidi = stripped.replace(/\p{Bidi_Control}/gu, '');
  const collapsed = noBidi.replace(/\s+/g, ' ').trim();

  // ⛔ "NON-EMPTY" IS NOT "VISIBLE", and an earlier version conflated them.
  // MEASURED as accepted before this line existed: U+200B alone, U+00AD alone,
  // U+200C+U+200D, U+2060, or combining marks alone -- all yielding a 201 with
  // a value that renders as nothing. `\s` does not match zero-width
  // characters, so a length test never fired. For the actor that defeated the
  // stated property outright: an invisible actor IS an anonymous one, and M3's
  // CHECK (btrim(actor) <> '') would have inherited the hole, since btrim does
  // not strip these either.
  //
  // Cf = format characters, Mn = nonspacing combining marks, Zs = space
  // separators. If nothing remains once those go, nothing is readable.
  //
  // ⛔ Cf/Mn/Zs IS NOT ENOUGH ON ITS OWN. Measured as ACCEPTED (201, stored,
  // rendering as nothing): U+3164 HANGUL FILLER, U+115F HANGUL CHOSEONG
  // FILLER, U+2800 BRAILLE PATTERN BLANK. They are categories Lo and So --
  // LETTERS and SYMBOLS to Unicode -- so no property class that admits real
  // names can exclude them.
  //
  // ⚠️ A "must contain a visibly-rendering character" positive test is the
  // shape one wants and is NOT EXPRESSIBLE: U+3164 is \p{Lo}, so requiring a
  // letter admits it. So this is a bounded DENYLIST of the known
  // blank-rendering code points, named rather than hand-waved, and it is
  // stated as a denylist so nobody mistakes it for exhaustive.
  const BLANK_RENDERING = /[\u115F\u1160\u3164\uFFA0\u2800\u180E]/gu;
  if (collapsed.replace(BLANK_RENDERING, '').replace(/[\p{Cf}\p{Mn}\p{Zs}]/gu, '').length === 0) {
    return null;
  }
  return collapsed;
}

/**
 * ⭐⭐ DO TWO TYPED NAMES DENOTE THE SAME ROSTER RESIDENT? — T1717 M4, Output Gate
 * round 2.
 *
 * ⛔ DERIVED FROM THE SERVER, NOT INVENTED. The store's rule is a COMPOSITION of
 * two steps and neither half is the whole rule:
 *   1. the route cleans every name with `cleanStoredText` above BEFORE it is
 *      stored (interior whitespace collapsed, control characters and bidi
 *      overrides gone), and
 *   2. `residents-repo.ts`'s `sameNameInHome` then compares
 *      `lower(btrim(display_name)) = lower(btrim($2))`.
 * `btrim` folds the ENDS ONLY - it does NOT collapse an interior run - so SQL
 * alone would call `Ann  Byrne` and `Ann Byrne` different people. The interior
 * collapse is step 1's, which is why the property only holds END TO END. Applying
 * `cleanStoredText` first makes the subsequent `btrim` a no-op, so
 * `cleanStoredText(x)?.toLowerCase()` reproduces the composition exactly.
 *
 * ⛔ IT EXISTS BECAUSE THE CLIENT HAD A **THIRD** RULE AND IT STRANDED A NURSE.
 * `RequestForm`'s `matchResident` compared `trim().toLowerCase()`. A name typed
 * with a double space was cleaned on the way in, so the resident was stored
 * collapsed - and the typed text then matched NOTHING on the way back. On a
 * retry the R1 settle fired, cleared the fields authored under that resident, and
 * left her with required-field errors and no way forward: the server refuses a
 * second create as `resident_exists`, correctly, so the screen was a dead end.
 *
 * ⚠️ A NAME THAT CLEANS TO NOTHING MATCHES NOTHING, including another such name.
 * Two invisible strings are not "the same resident"; they are both no resident.
 *
 * ⚠️⚠️ THE CASE FOLD IS JS `toLowerCase()` AGAINST POSTGRES `lower()`, AND THEY
 * ARE NOT THE SAME FUNCTION — T1717 M7, Captain deltas #33. JS folds per the
 * Unicode default case-conversion table; PG's `lower()` folds per the database
 * COLLATION. They agree across Latin-1 and the scripts this portal sees, and
 * diverge on at least: the Turkish dotted/dotless I (`I` → `i` in JS, → `ı`
 * under a `tr_TR` collation), `ẞ` U+1E9E (JS → `ß`; several ICU collations agree
 * but the C locale leaves it alone), and a handful of Cherokee/Deseret pairs.
 * ⇒ WHAT A DIVERGENCE COSTS, and it is bounded: this client-side check would say
 * "not the same person", the nurse would go on to create, and the SERVER's
 * `sameNameInHome` would answer 409 `resident_exists` with no "put them back"
 * offer — the same narrow create-then-409 dead end M4's fix removed for the
 * whitespace case. It cannot store two rows for one person (the server is the
 * decider), and it cannot leak anything.
 * ⛔ NOT FIXED HERE, deliberately: the honest fix is to ask the SERVER (the
 * decider) rather than to grow a third fold in the client, and no such route
 * exists yet. The trigger to build one is a real name in this class reaching a
 * live roster — production is Irish nursing homes under the database's default
 * collation, so the population is currently empty.
 */
export function sameStoredName(a: string, b: string): boolean {
  const left = cleanStoredText(a);
  const right = cleanStoredText(b);
  return left !== null && right !== null && left.toLowerCase() === right.toLowerCase();
}

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * ⛔⛔ THE ONLY SHAPE EITHER INSTANT MAY TAKE ON THE WIRE — T1717 M5 security
 * review (Med), and it binds BOTH legs because the defect pre-existed on the
 * outbound.
 *
 * ⛔ WHAT WAS WRONG. The gate was `!Number.isNaN(new Date(x))` and the RAW
 * string was then stored. `new Date` accepts an enormous, implementation-defined
 * grammar, so all of these were ACCEPTED and written verbatim:
 *   · `'Sat Aug 15 2026 18:30:00 GMT+0100 (<arbitrary text, newlines>)'` — the
 *     parenthesised tail is free text. Postgres' timestamptz grammar then
 *     refuses it (22007) and the unmapped throw carries the attacker's string
 *     into the operator log and into Sentry.
 *   · `'2027'` — a bare year, silently midnight on 1 January.
 *   · `'2026-08-15T17:30:00'` — no zone marker, so it is read in the SERVER's
 *     local zone while every other instant in the system is UTC.
 *   · `'+020026-…'` and `'+275760-09-13T00:00:00.000Z'` — expanded years.
 *
 * ⛔ SO: a strict ISO-8601 UTC instant, four-digit year, `Z` only, optional
 * milliseconds — and the value that is STORED is `toISOString()`, never the
 * caller's bytes. Canonicalising is the half that matters as much as the regex:
 * two spellings of one instant must not become two different strings in a column
 * that other code compares.
 * ⚠️ AN OFFSET FORM (`…+01:00`) IS REFUSED RATHER THAN NORMALISED. It is a real
 * ISO instant, but nothing in this system emits one — `dublinInstant` composes
 * every leg with `toISOString()` — so accepting it would widen the grammar for
 * no caller. Widen here, deliberately, if one ever appears.
 * ⭐ Board 2638: no instant rides the request wire any more (the browser sends
 * the Irish day and time). This regex now also RECOGNISES the old bundle: a
 * `journeyDate` it matches is a stale page, refused by `STALE_PAGE_MESSAGE`.
 */
const ISO_INSTANT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

/**
 * The wire form → the canonical instant, or null if it is not one.
 *
 * ⚠️ THE REGEX ALONE IS NOT ENOUGH: `'2026-08-15T25:00:00Z'` and
 * `'2026-13-01T00:00:00Z'` both match it and both are `Invalid Date`, so the
 * parse still has to be checked. Two gates, not one.
 *
 * ⛔⛔ AND THIS BLOCK NAMED A THIRD CASE THAT IS **NOT** REFUSED — corrected at
 * T1868 M5, MEASURED on node v24.18.1 rather than reasoned. It said
 * `'2026-02-31T00:00:00Z'` is `Invalid Date`. It is not: V8 ROLLS AN
 * OUT-OF-RANGE **DAY** OVER, so that value parses to `2026-03-03T00:00:00.000Z`
 * and this function returns it, canonicalised. Only the HOUR (`25`) and the
 * MONTH (`13`) overflow to `Invalid Date`.
 * ⇒ THE CONSEQUENCE, STATED PLAINLY: a non-browser caller posting a 31st of
 * February books the 3rd of March, silently, and every downstream bound then
 * applies to the ROLLED date rather than the typed one. It is not reachable from
 * `RequestForm` (an `<input type="date">` cannot produce it) and it is not a new
 * defect — it is `Date`'s documented behaviour, and refusing it would need a
 * calendar round-trip check this validator has never had.
 * ⛔ IT IS PINNED RATHER THAN FIXED (`request-routes.spec.ts`), because a
 * comment that claims a guard which does not fire is how the next reader stops
 * checking — which is exactly what happened here.
 *
 * ⛔⛔ AND THE FIRST CORRECTION OVER-CLAIMED IN ITS TURN, WHICH IS THE PART WORTH
 * READING. It said the rolled value "can only ever earn the past/window refusal,
 * because no month short enough to roll lands inside `MAX_ADVANCE_DAYS`". FALSE,
 * disproved by execution at the M5 security review: any 30-day month rolls to
 * the 1st of the NEXT one, which is ordinarily well inside 28 days. Measured on
 * v24.18.1 with `now = 2026-09-08`:
 *   `pickupAt:    '2026-09-31T09:00:00Z'` → **ACCEPTED**, stored `2026-10-01T09:00:00.000Z`
 *   `journeyDate: '2026-09-31T09:00:00Z'` → **ACCEPTED**, stored `2026-10-01T09:00:00.000Z`
 * ⇒ a rolled date is ACCEPTED AND STORED on the ordinary path, not merely
 * refused for a different reason. The clause was reasoned from one fixture's
 * frozen clock (July, where the window ends inside two 31-day months) and then
 * stated as a property of the validator. Two arms now pin it: the February one,
 * which lands in the past, and a September one, which lands INSIDE the window
 * and is stored.
 * ⚠️ Impact is unchanged and still Low — `<input type="date">` cannot produce a
 * 31st of September, and `journeyDate` has behaved this way since long before
 * M5. What changed is that the comment no longer says something a reader can
 * disprove in one command. UPGRADE TRIGGER: any caller
 * other than the form gaining the ability to send a hand-typed date string.
 * ⭐ CLOSED FOR THE REQUEST WIRE BY BOARD 2638: no wire value reaches this
 * function now. `journeyDate` is a day that `dublinInstant` refuses when it
 * does not survive a calendar round trip (`2026-09-31` is refused, not rolled),
 * and every instant handed here is `dublinInstant`'s own output.
 */
function parseIsoInstant(raw: unknown): string | null {
  if (typeof raw !== 'string' || !ISO_INSTANT_RE.test(raw)) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

// ⚠️ `DATE_ONLY_RE` STOOD HERE (T632): a bare `YYYY-MM-DD` journeyDate was
// refused, because Postgres casts one to MIDNIGHT and stores a time nobody
// stated. Board 2638 makes the bare Irish day THE wire shape, and T632's defect
// still cannot recur: the day is never stored alone, it is composed with a
// REQUIRED `journeyTime` (`dublinInstant` checks the day's shape and calendar).
const ROOM_MAX_LEN = 20;
// ⚠️ `NEXT_OF_KIN_NAME_MAX_LEN` STOOD HERE and is deleted with the field it
// bounded (T1868 M6). ⛔ MIGRATION v11's `requests_next_of_kin_name_len` CHECK
// IS STILL ON THE TABLE — the column is not dropped — so the constant's absence
// is "nothing writes this column any more", never "the database stopped caring".
const NOTES_MAX_LEN = 500;
// EXPORTED since board 2190 so `RequestForm.tsx` can derive its input bound
// from it instead of hand-keying a second number that drifts.
export const DESTINATION_TEXT_MAX_LEN = 120;

/**
 * How much `formatDestination` can ADD to what the nurse typed: one space
 * inserted into an unspaced Eircode, plus a canonical separator replacing a
 * one-character one. Worst cases, measured: `Bray a98x123` and `Bray,a98x123`,
 * both exactly +2. The hint only ever shrinks.
 *
 * The form's input bound is `DESTINATION_TEXT_MAX_LEN - DESTINATION_TEXT_GROWTH`
 * so the browser cap can never produce a value this validator then refuses.
 */
export const DESTINATION_TEXT_GROWTH = 2;

/**
 * ONE message for both "you typed nothing" and "that is not an Eircode".
 *
 * Deliberately one and not two: both failures need the same instruction, and a
 * nurse who typed a plain place name is not making a different mistake from one
 * who typed nothing. It carries a worked example of BOTH shapes because the box
 * accepts an Eircode alone or an Eircode plus a place, and a refusal that does
 * not show the accepted shape is a wall (CLAUDE.md: the form is an option,
 * never a wall).
 */
export const DESTINATION_EIRCODE_MESSAGE =
  'Enter the destination Eircode, like A98 X123. You can add a place after it, ' +
  'like A98 X123, Bray seafront.';

/** How far ahead a one-off run may be booked (2026-07-21 review, P8; Q44
 *  asks Owen to confirm the number). Applies to the next concrete date, not
 *  a pattern's lifetime — a standing weekly run books itself forward. */
export const MAX_ADVANCE_DAYS = 28;

/**
 * Board 2788 (plan DD-19, S23, Alex's rulings of 2026-10-06 with Helen and Owen):
 * THE 24-HOUR CUT-OFF. Within this many hours of a request's pickup the HOME may
 * not change it, ask to change it, ask to cancel it, or cancel it directly; it
 * rings instead. The pickup is `COALESCE(pickup_at, journey_date)`, as instants
 * (24 hours of elapsed time, never "the day before" on a wall clock), and the
 * NEW pickup a change or a change ask derives must clear the same window.
 * ⛔ ONE NAME FOR THE WINDOW: the three nurse-door writers and the nurse-door
 * cancel in `core/requests-repo.ts` read it through `changeCutoffBound`, and the
 * refusal sentence below is built from it. Helen's own doors (`rescheduleRequest`,
 * `decideRequestAsk`, the ops status route) are NOT subject, and a repeating
 * request (no single pickup) is not either.
 */
export const CHANGE_CUTOFF_HOURS = 24;

/** The instant a pickup must be strictly AFTER for the home to act on it: `now`
 *  plus `CHANGE_CUTOFF_HOURS`. A pickup exactly on it is inside the cut-off. */
export function changeCutoffBound(now: Date): Date {
  return new Date(now.getTime() + CHANGE_CUTOFF_HOURS * 60 * 60 * 1000);
}

/** Whether a pickup instant is at or within the cut-off of `now`. A null pickup
 *  (a repeating request, which has no single one) is never inside it. */
export function insideChangeCutoff(pickup: Date | string | null, now: Date): boolean {
  if (pickup === null) return false;
  return new Date(pickup).getTime() <= changeCutoffBound(now).getTime();
}

/**
 * ⛔ East Coast Minibus's phone number, ONE portal source (board 2788, plan
 * DD-19). The cut-off sentence below is the first reader; plan M5 makes the card's
 * `tel:` link read it too and pins both forms against `site/src/data/site.ts`
 * (`phoneDisplay`, `phoneHref`) with a parity test, so the two cannot drift. Until
 * then nothing pins it: change it only together with `site.ts`.
 */
export const ECMB_PHONE = { display: '086 601 6572', href: 'tel:+353866016572' } as const;

/** The refusal every nurse door answers inside the cut-off (409). Plain words,
 *  with the number, so the home can ring and East Coast Minibus can organise it. */
export const CHANGE_CUTOFF_MESSAGE =
  `This trip is less than ${String(CHANGE_CUTOFF_HOURS)} hours away: please ring us on ${ECMB_PHONE.display}.`;

/**
 * How long a request row is kept after its journey: `purge_after` is the
 * journey instant (or the send, for a row with no date) plus this many calendar
 * months, and `purgeExpired` (core/db.ts) deletes the row on that clock.
 * ⭐ Board 2788 MOVED IT HERE from `core/requests-repo.ts`, with `addMonths`
 * below, rather than copying it: a change of date re-stamps `purge_after`
 * (plan S5), and `deriveJourneyChange` is the one place that derivation lives.
 * `insertRequest` imports both, so the create path and the change path cannot
 * keep two clocks.
 */
export const PURGE_MONTHS = 24;

/** Adds `months` calendar months to `date`. JS's Date.setMonth normalises a
 *  month index > 11 into the following year(s), so this handles the
 *  24-month (2-year) purge window as a single arithmetic step with no
 *  manual year-rollover logic; chosen over `+ 24*30 days` because it keeps
 *  the purge date aligned to a real calendar anniversary (e.g. 2026-07-20 ->
 *  2028-07-20) regardless of the originating month's length. */
export function addMonths(date: Date, months: number): Date {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

/**
 * The outbound instant's two clock bounds, as ONE function with two callers
 * (board 2788, plan M2 step 6): `validateRequestInput` on create and
 * `deriveJourneyChange` on a change. Extracted, never copied, for the reason
 * `validateReturnInstant` gives: two windows that can drift apart is how one of
 * them stops being enforced. The messages are the create path's, byte for byte.
 * `lowerBoundsOnly` skips the upper bound (the browser's clock may lag).
 */
function journeyBoundError(
  iso: string,
  now: Date,
  lowerBoundsOnly: boolean,
): { code: 'past' | 'too_far'; error: string } | null {
  if (Date.parse(iso) <= now.getTime()) {
    return { code: 'past', error: 'Journey date must be in the future.' };
  }
  if (!lowerBoundsOnly && Date.parse(iso) > now.getTime() + MAX_ADVANCE_DAYS * 24 * 60 * 60 * 1000) {
    return { code: 'too_far', error: `Pick a date within the next ${String(MAX_ADVANCE_DAYS / 7)} weeks.` };
  }
  return null;
}

/**
 * ⛔⛔ THE PICKUP'S LOWER BOUND, AND IT CLOSES A GAP THIS FILE ALREADY NAMED
 * (security review M1, board 2188). The block that validates `pickupAt` says in
 * its own words that *"a pickup is bounded ABOVE by the appointment and by
 * nothing below"* — that was true, and it meant a hand-built POST could place
 * the instant anywhere inside the 28-day window and have it render as an
 * ordinary clock on reception's card and Helen's dispatch table.
 *
 * ⚠️ THE NUMBER IS NOT A NEW POLICY — it is the one the DATABASE already allows,
 * mirrored so the accepted set matches what the derivation can actually produce:
 * `homes_pickup_offset_minutes_range CHECK (pickup_offset_minutes > 0 AND
 * pickup_offset_minutes <= 480)` (`core/migrations.ts`). A home configured at the
 * legal maximum derives a pickup exactly 480 minutes early, so the bound must be
 * `<`, not `<=`, or this refuses a booking that home is entitled to make.
 * ⚠️ Keep the two in step: widening the DB CHECK without widening this refuses
 * legitimate bookings, and widening this alone re-opens the gap.
 * ⭐ Board 2638: the server now derives the pickup itself (`pickupInstantFor`),
 * so this bounds the HOME's offset as read, no longer a client-supplied instant.
 */
export const MAX_PICKUP_OFFSET_MINUTES = 480;

/**
 * ⭐⭐ Board 2638 (plan D1, D4) — THE COLLECTION: the appointment INSTANT minus the
 * home's offset, never the wall clock, so across a clock change the real lead
 * time is exactly the offset (a 02:30 GMT appointment 120 minutes early is
 * collected at 01:30 IST, the first 01:30). ONE function for the server's
 * derivation and the form's preview (`request-form-logic.ts` `derivePickup`),
 * run over one column. Null when there is nothing to derive from: an offset that
 * is null, not finite or not positive, or an appointment that is not an instant.
 */
export function pickupInstantFor(journeyIso: string, offsetMinutes: number | null): string | null {
  if (offsetMinutes === null || !Number.isFinite(offsetMinutes) || offsetMinutes <= 0) return null;
  const appointment = Date.parse(journeyIso);
  if (Number.isNaN(appointment)) return null;
  return new Date(appointment - offsetMinutes * 60_000).toISOString();
}

/**
 * The collection DERIVED AND BOUNDED, as ONE chain with two callers (board 2788,
 * plan M2 step 6): `validateRequestInput` on create and `deriveJourneyChange` on
 * a change, so a changed appointment is bounded exactly as a new one is. Moved
 * here from the validator's `pickupAt` block, with its reasoning, rather than
 * copied; the five messages are carried byte for byte.
 */
function derivePickupChecked(
  journeyIso: string,
  offset: number | null,
  now: Date,
  lowerBoundsOnly: boolean,
): { ok: true; pickupAt: string; offsetUsed: number } | { ok: false; error: string } {
  const windowEnd = now.getTime() + MAX_ADVANCE_DAYS * 24 * 60 * 60 * 1000;
  const iso = pickupInstantFor(journeyIso, offset);
  if (iso === null || offset === null) {
    // ⛔ STILL REQUIRED. `portal.homes.pickup_offset_minutes` is `NOT NULL
    // DEFAULT 60` from v12, so a real home always derives one; a null or
    // non-positive offset reaches here only from a mirror, a fixture or a
    // caller that passed none. Absence is not an answer: the fact P0-4 exists
    // to put in front of Helen would be missing.
    return { ok: false, error: 'We could not work out the collection. Try again, or ring East Coast Minibus.' };
  }
  if (Date.parse(iso) >= Date.parse(journeyIso)) {
    // ⛔ AS INSTANTS, and STRICTLY before: v12's
    // `requests_pickup_before_appointment` is strict, so an equal pair would be
    // a bare 500. It cannot fire while `pickupInstantFor` subtracts a positive
    // offset from the instant; it is what catches a build that stops. (D4: on
    // the instant, a spring-forward night cannot collapse the lead time to zero
    // the way a wall-clock derivation did; `test/client/dst-composition.spec.ts`.)
    return { ok: false, error: 'The pickup must be before the appointment.' };
  }
  if (Date.parse(iso) <= now.getTime()) {
    // ⛔⛔ THE BOUND `returnAt` DOES NOT NEED, AND IT IS NOT AN EXTRA. A
    // return is bounded below by transitivity (`returnAt > journeyDate >
    // now`); a pickup is bounded ABOVE by the appointment and by nothing
    // below, so an offset of up to 480 minutes against an appointment that
    // need only be in the FUTURE derives a pickup that has already happened.
    // ⭐ A LOWER bound, so it applies under `lowerBoundsOnly` too (plan D3).
    return { ok: false, error: 'That appointment is too soon for the taxi to collect in time. Ring East Coast Minibus.' };
  }
  if (!lowerBoundsOnly && Date.parse(iso) > windowEnd) {
    // ⛔ ONE WINDOW, ONE CONSTANT, ONE SENTENCE: the same three the outbound
    // and the return share, so the legs cannot drift apart. It cannot fire
    // while the pickup is before an outbound already inside the window; an
    // UPPER bound, so `lowerBoundsOnly` skips it.
    return { ok: false, error: `Pick a date within the next ${String(MAX_ADVANCE_DAYS / 7)} weeks.` };
  }
  if (Date.parse(iso) < Date.parse(journeyIso) - MAX_PICKUP_OFFSET_MINUTES * 60 * 1000) {
    // ⛔⛔ THE FIFTH BOUND (security review M1, board 2188), LAST IN THE CHAIN
    // so the older messages keep their precedence. Since 2638 it bounds the
    // HOME's offset as read: one past v12's own 480 CHECK (which a pg-mem
    // mirror omits) is refused rather than stored. The strict `<` admits
    // exactly 480, which a home at the database's maximum is entitled to.
    return { ok: false, error: 'The pickup is too far before the appointment. Ring East Coast Minibus.' };
  }
  return { ok: true, pickupAt: iso, offsetUsed: offset };
}

/** What `validateReturnInstant` answers: the canonical instant, or the ONE
 *  field message its caller reports. ⛔ IT NEVER THROWS — both callers are
 *  collecting field errors rather than handling exceptions (the create path
 *  fills an `errors` record, the edit route answers 400 with one), and a throw
 *  would put the window's enforcement behind a try/catch in each of them. */
export type ReturnInstantResult =
  | { ok: true; iso: string }
  | { ok: false; error: string };

/**
 * ⭐⭐ T2088 M5 — THE RETURN INSTANT'S THREE CHECKS, IN ONE PLACE, BECAUSE THERE
 * ARE TWO CALLERS NOW. They were inlined in `validateRequestInput`'s return
 * branch below while the create path was the only way an instant could ever be
 * stored. M5's edit route (`POST /api/requests/:id/return-time`,
 * `server/request-routes.ts`) stores one too, and this file's own standing
 * reason forbids the copy that would otherwise have been made: two windows that
 * can drift apart is how one of them stops being enforced. So it is EXTRACTED
 * and called twice, never re-implemented — and the create path's three messages
 * are carried byte-for-byte, so the move is invisible to the nurse and to the
 * specs that pin those sentences.
 *
 * ⛔ `now` IS A PARAMETER AND THIS FUNCTION READS NO CLOCK. The module bundles
 * to the browser, `validateRequestInput` already takes `opts.now`, and the edit
 * route passes `deps.now()`. A `Date.now()` in here would be unreachable from
 * either caller's fixtures, so the advance window's boundary could not be
 * graded at offset zero — and a boundary fixture that cannot sit ON the
 * boundary grades nothing.
 *
 * ⛔⛔ `outbound === null` SKIPS THE ORDERING CHECK, IT HAS EXACTLY ONE
 * CONSUMER, AND IT IS NOT DEAD CODE. `validateRequestInput` passes its
 * `journeyDate` local, which is null exactly when the request's OWN date failed
 * validation; the skip is deliberate, so a nurse already holding a refusal for
 * her day is not ALSO told the return is before an outbound that never parsed.
 * ⛔ THE EDIT ROUTE CAN NEVER PASS NULL — AND THE REASON IS THE PREDICATE, NOT
 * THE SCOPE CHECK. `journey_date` is NULLABLE and IS null on every recurring
 * row (v1's `one_off_xor_pattern`), so "the row is in scope" establishes
 * nothing about the column: a scoped recurring row would hand this function a
 * null outbound and take the skip. What makes the claim true is
 * `setRequestReturnAt`'s `AND schedule_pattern IS NULL` conjunct (the M5-rework
 * security fix): the only rows that predicate will move are one-off ones, which
 * carry a non-NULL `journey_date` by that same XOR. ⚠️ Since board 2638 the
 * route also answers 409 on a null outbound BEFORE it reaches here (it composes
 * the return on the outbound's Irish day, and a null has none), so a recurring
 * row never takes the skip. ⇒ The arm belongs to the create path alone.
 * Deleting it as unreachable would make that one caller double-report.
 * ⚠️ An outbound that is present but unparseable compares as NaN and therefore
 * passes this check — unchanged from the inline form, and unreachable from both
 * callers (one has a canonical ISO string, the other a TIMESTAMPTZ).
 *
 * ⭐ Board 2638 (plan D3): `now === null` skips the advance window and nothing
 * else. The browser passes null under `lowerBoundsOnly`, because its clock is
 * an estimate that may lag; both server callers pass `deps.now()`. Since 2638
 * both callers also hand this `dublinInstant`'s output, never wire bytes.
 */
export function validateReturnInstant(
  rawReturnAt: unknown,
  outbound: Date | string | null,
  now: Date | null,
): ReturnInstantResult {
  const iso = parseIsoInstant(rawReturnAt);
  if (iso === null) {
    return { ok: false, error: 'The return must be a valid date and time.' };
  }
  if (outbound !== null && new Date(iso).getTime() <= new Date(outbound).getTime()) {
    // ⛔ AS INSTANTS, NEVER AS STRINGS. Both sides were composed in the same
    // browser, and `'2026-09-01T23:00:00.000Z' < '2026-09-02T01:00:00.000Z'`
    // happens to agree with the instants only while both are ISO-Z. v11's
    // `requests_return_after_outbound` refuses an inverted row, so the
    // failure would be a bare 500 rather than a field error.
    // ⭐⭐ T2088 M5 — AND THAT CHECK IS WHY THE EDIT ROUTE'S `WHERE` CLAUSE
    // DOES **NOT** REPEAT THIS RULE. The database owns it on every ONE-OFF row;
    // a third owner in a predicate would be the drift this file warns about, and
    // would turn an impossible-state 23514 into a silent refusal.
    // ⚠️ NOT "unconditionally": the CHECK is VACUOUS ON A RECURRING ROW, by
    // construction (`migrations.ts` says so in capitals — `one_off_xor_pattern`
    // makes `journey_date` NULL exactly when `schedule_pattern` is set, so the
    // CHECK's own `journey_date IS NULL` disjunct passes there). That row is
    // refused by `setRequestReturnAt`'s `AND schedule_pattern IS NULL` conjunct
    // instead — a DIFFERENT rule, not a second owner of this one.
    return { ok: false, error: 'The return must be after the outbound trip.' };
  }
  if (now !== null && new Date(iso).getTime() > now.getTime() + MAX_ADVANCE_DAYS * 24 * 60 * 60 * 1000) {
    // ⛔⛔ THE RETURN SHARES THE OUTBOUND'S WINDOW EXACTLY — PM ruling on the
    // M5 security review (Low/Med), recorded beside DD4. Until it did, the
    // return was bounded only by `> journeyDate`, so
    // `'+275760-09-13T00:00:00.000Z'` was a storable "return" on a journey
    // four weeks away. ⛔ NO NEW NUMBER AND NO SECOND SENTENCE: the same
    // `MAX_ADVANCE_DAYS` and the same copy the outbound uses, because two
    // windows that can drift apart is how one of them stops being enforced.
    return { ok: false, error: `Pick a date within the next ${String(MAX_ADVANCE_DAYS / 7)} weeks.` };
  }
  return { ok: true, iso };
}

/**
 * What a change to an existing request asks for (board 2788, plan M2 step 6,
 * DD-3, DD-11, DD-12): the request row's own facts, the home's CURRENT offset,
 * and the Irish day and `HH:MM` the caller typed.
 * ⛔ `journeyDate` (the row's CURRENT outbound) is an input the plan's list does
 * not name; DD-12 needs it, because a legacy return on another Dublin day than
 * its outbound is refused and the day is read off this instant.
 */
export type JourneyChangeInput = {
  timeMeaning: TimeMeaning | null;
  journeyDate: Date | string | null;
  returnAt: Date | string | null;
  schedulePattern: SchedulePattern | null;
  pickupOffsetMinutes: number | null;
  day: unknown;
  time: unknown;
  now: Date;
};

/** Every column a change of date moves, in ONE statement (plan S5). */
export type JourneyChange = {
  journeyDate: string;
  pickupAt: string | null;
  offsetUsed: number | null;
  returnAt: string | null;
  purgeAfter: string;
};

/** Why a change cannot be derived. `code` is for the caller's own words (the
 *  kiosk words a return clash for Helen, DD-12); `error` is the portal's
 *  sentence and `field` the key the create path would use. `returnTime` is the
 *  Irish `HH:MM` of the return that would clash, for the words. */
export type JourneyChangeRefusal = {
  ok: false;
  code: 'repeating' | 'invalid_day' | 'invalid_time' | 'past' | 'too_far' | 'pickup' | 'return_clash' | 'return_window';
  field: 'journey' | 'journeyDate' | 'journeyTime' | 'pickupAt' | 'returnAt';
  error: string;
  returnTime?: string;
};

export type JourneyChangeResult = { ok: true; value: JourneyChange } | JourneyChangeRefusal;

/**
 * ⭐⭐ Board 2788 (plan M2 step 6) - THE ONE DERIVATION OF A CHANGED JOURNEY,
 * shared by the home's change, its change ask and Helen's reschedule, so the
 * portal stays the only place a pickup is worked out (DD-3). Pure, no clock
 * read, client-bundlable like the rest of this module.
 * ⛔ THE SAME KIND OF TIME THE REQUEST WAS MADE WITH (DD-11): the typed time is
 * the row's own `time_meaning`. On `appointment` the collection is derived with
 * the home's CURRENT offset through `derivePickupChecked`, exactly as a new
 * request is (so a legacy row with a NULL `pickup_offset_minutes_used` works);
 * on `collection` (and a legacy NULL meaning) there is no second time.
 * ⛔ THE CREATE PATH'S BOUNDS, EXTRACTED (`journeyBoundError`,
 * `derivePickupChecked`, `validateReturnInstant`): in the future, at most
 * `MAX_ADVANCE_DAYS` ahead.
 * ⛔ A SET RETURN KEEPS ITS DUBLIN WALL CLOCK ON THE NEW DAY (DD-12), composed by
 * `dublinWallClock` then `dublinInstant`, never `+N x 24h` (an hour wrong across
 * the last Sunday in October). If it is then not after the new outbound, or the
 * stored return was on another Dublin day than the stored outbound (a legacy
 * row), the change is refused: neither side has a door that edits a set return.
 * `purgeAfter` is the new outbound plus `PURGE_MONTHS`, as `insertRequest`
 * stamps it.
 */
export function deriveJourneyChange(input: JourneyChangeInput): JourneyChangeResult {
  if (input.schedulePattern !== null || input.journeyDate === null) {
    return {
      ok: false,
      code: 'repeating',
      field: 'journey',
      error: 'A repeating trip has no single date to change. Ring East Coast Minibus.',
    };
  }
  const { day, time } = input;
  if (typeof day !== 'string' || dublinInstant(day, '00:00') === null) {
    return { ok: false, code: 'invalid_day', field: 'journeyDate', error: 'Journey date must be a valid date.' };
  }
  const journeyDate = typeof time === 'string' ? dublinInstant(day, time) : null;
  if (journeyDate === null) {
    return { ok: false, code: 'invalid_time', field: 'journeyTime', error: 'Enter the time in 24-hour format, like 09:30.' };
  }
  const bound = journeyBoundError(journeyDate, input.now, false);
  if (bound !== null) {
    return { ok: false, code: bound.code, field: 'journeyDate', error: bound.error };
  }

  let pickupAt: string | null = null;
  let offsetUsed: number | null = null;
  if (input.timeMeaning === 'appointment') {
    const pickup = derivePickupChecked(journeyDate, input.pickupOffsetMinutes, input.now, false);
    if (!pickup.ok) {
      return { ok: false, code: 'pickup', field: 'pickupAt', error: pickup.error };
    }
    pickupAt = pickup.pickupAt;
    offsetUsed = pickup.offsetUsed;
  }

  let returnAt: string | null = null;
  if (input.returnAt !== null) {
    const storedReturn = dublinWallClock(input.returnAt);
    const storedOutbound = dublinWallClock(input.journeyDate);
    const clash = (returnTime: string): JourneyChangeRefusal => ({
      ok: false,
      code: 'return_clash',
      field: 'returnAt',
      // Board 2788 DD-12's words for the home's card; flagged for Vicky's sitting.
      error: `The return at ${returnTime} would be before the new time: please ring us.`,
      returnTime,
    });
    if (storedReturn === null || storedOutbound === null) {
      // Unreachable from a TIMESTAMPTZ column; the create path's sentence for a
      // return it could not work out.
      return {
        ok: false,
        code: 'return_clash',
        field: 'returnAt',
        error: 'We could not work out the return time. Try again, or ring East Coast Minibus.',
      };
    }
    if (storedReturn.day !== storedOutbound.day) {
      // A legacy return on another day (written before the same-day rule).
      return clash(storedReturn.time);
    }
    const moved = dublinInstant(day, storedReturn.time);
    if (moved === null || Date.parse(moved) <= Date.parse(journeyDate)) {
      return clash(storedReturn.time);
    }
    const checked = validateReturnInstant(moved, journeyDate, input.now);
    if (!checked.ok) {
      return { ok: false, code: 'return_window', field: 'returnAt', error: checked.error };
    }
    returnAt = checked.iso;
  }

  return {
    ok: true,
    value: {
      journeyDate,
      pickupAt,
      offsetUsed,
      returnAt,
      purgeAfter: addMonths(new Date(journeyDate), PURGE_MONTHS).toISOString(),
    },
  };
}

function isPositiveInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v > 0;
}

function asRecord(input: unknown): Record<string, unknown> {
  return typeof input === 'object' && input !== null && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : {};
}

/** A wire value that is "said": not absent, null or `''`. */
function isPresent(v: unknown): boolean {
  return v !== undefined && v !== null && v !== '';
}

export function validateRequestInput(input: unknown, opts: ValidateRequestOptions): ValidateRequestResult {
  const body = asRecord(input);

  // ⛔⛔ Board 2638 (plan D2) — A STALE PAGE IS REFUSED FIRST AND ALONE, before any
  // other field is judged, so the one message the old bundle shows is the one
  // that tells her what to do. Its fingerprint: an ISO-instant `journeyDate`, or
  // a `pickupAt` / `returnAt` value (the new form sends neither key).
  // ⚠️ Ahead of the recurrence refusal too: a stale body never carries a pattern
  // (the old bundle sends `schedulePattern: null`), and this refusal must stand alone.
  const rawJourneyDateForStale = body['journeyDate'];
  if (
    (typeof rawJourneyDateForStale === 'string' && ISO_INSTANT_RE.test(rawJourneyDateForStale)) ||
    isPresent(body['pickupAt']) ||
    isPresent(body['returnAt'])
  ) {
    return { ok: false, errors: { journey: STALE_PAGE_MESSAGE } };
  }

  const errors: Record<string, string> = {};
  // ⛔ AN OWN KEY, NEVER A PROTOTYPE WALK (security review L1, board 2638): the
  // server's options object does not carry this key, so a plain read would let a
  // polluted `Object.prototype.lowerBoundsOnly` switch the server's upper bounds off.
  const lowerBoundsOnly = Object.hasOwn(opts, 'lowerBoundsOnly') && opts.lowerBoundsOnly === true;

  // residentId
  let residentId = 0;
  if (isPositiveInt(body['residentId'])) {
    residentId = body['residentId'] as number;
  } else {
    errors['residentId'] = 'Select a resident.';
  }

  // requestedByStaffId — REQUIRED since T2075 M3 (Step 2, R-3), the `residentId`
  // shape: absent, null and anything that is not a bounded positive integer all
  // answer the ONE key with the ONE message. ⛔ BOUNDED LIKE EVERY ID THAT
  // REACHES A BIGINT PARAMETER: `isPositiveInt` alone admits `1e30`, which
  // Postgres refuses as out-of-range and surfaces as a bare 500 (the
  // `request-routes.ts` `isIdLike` lesson); MAX_SAFE_INTEGER is the bound
  // because the value round-trips through JSON as a JS number.
  // ⚠️ THE MESSAGE IS `client/copy.ts` `requesterRequired`, byte for byte, and
  // `test/client/request-form.spec.tsx` pins the two equal: the form renders
  // whatever this function said, so the sentence lives once.
  let requestedByStaffId = 0;
  const rawRequestedBy = body['requestedByStaffId'];
  if (isPositiveInt(rawRequestedBy) && rawRequestedBy <= Number.MAX_SAFE_INTEGER) {
    requestedByStaffId = rawRequestedBy;
  } else {
    errors['requestedByStaffId'] = 'Choose who is requesting this trip.';
  }

  // room
  let room = '';
  const rawRoom = body['room'];
  if (typeof rawRoom !== 'string' || rawRoom.trim().length === 0) {
    errors['room'] = 'Room is required.';
  } else if (rawRoom.length > ROOM_MAX_LEN) {
    errors['room'] = `Room must be ${String(ROOM_MAX_LEN)} characters or fewer.`;
  } else {
    room = rawRoom;
  }

  // equipment (optional; defaults to none)
  let equipment: string[] = [];
  const rawEquipment = body['equipment'];
  if (rawEquipment === undefined || rawEquipment === null) {
    equipment = [];
  } else if (!Array.isArray(rawEquipment) || !rawEquipment.every((e) => typeof e === 'string')) {
    errors['equipment'] = 'Equipment must be a list of options.';
  } else {
    const known: readonly string[] = EQUIPMENT_OPTIONS;
    const unknown = rawEquipment.filter((e) => !known.includes(e));
    if (unknown.length > 0) {
      errors['equipment'] = `Unknown equipment option: ${unknown.join(', ')}.`;
    } else {
      equipment = Array.from(new Set(rawEquipment));
    }
  }

  // destinationId / destinationText: exactly one of the two, XOR. An
  // empty-string destinationId (the unselected <select> option) counts as
  // absent, same as null/undefined.
  //
  // ⛔ SINCE BOARD 2190 (Alex, 2026-09-18) THE FREE-TYPED BRANCH IS NOT FREE
  // TEXT. It must lead OR end with a valid Eircode, so a driver always has an
  // exact location; an optional place hint may share the box
  // (`A98 X123, Bray seafront by the bandstand`) so a pier, a park, a beach or
  // a pitch stays bookable. What is STORED is the canonical reconstruction,
  // Eircode first, whatever order the nurse typed.
  //
  // ⚠️ THE LENGTH IS CHECKED TWICE, AGAINST ONE CONSTANT, AND BOTH ARE LOAD
  // BEARING. The RAW check keeps the refusing direction proven and bounds what
  // the parser ever sees; the RECONSTRUCTION check catches a raw value at
  // exactly the limit that `formatDestination` grows past it. Dropping either
  // one stores an over-long value or loses the ceiling's only proof.
  //
  // ⚠️ Legacy rows PREDATE this rule and are never re-validated on read
  // (`core/requests-repo.ts` projects `COALESCE(dest.name, r.destination_text)`
  // and reformats nothing), so a stored value is NOT guaranteed to parse.
  const rawDestinationId = body['destinationId'];
  const rawDestinationText = body['destinationText'];
  const hasDestinationId =
    rawDestinationId !== null && rawDestinationId !== undefined && rawDestinationId !== '';
  const hasDestinationText = rawDestinationText !== null && rawDestinationText !== undefined;

  let destinationId: number | null = null;
  let destinationText: string | null = null;

  if (hasDestinationId === hasDestinationText) {
    // Both present or both absent: neither is a valid choice.
    errors['destinationId'] = 'Select a destination.';
  } else if (hasDestinationId) {
    if (isPositiveInt(rawDestinationId)) {
      destinationId = rawDestinationId;
    } else {
      errors['destinationId'] = 'Select a destination.';
    }
  } else if (typeof rawDestinationText !== 'string' || rawDestinationText.trim().length === 0) {
    errors['destinationText'] = DESTINATION_EIRCODE_MESSAGE;
  } else if (rawDestinationText.trim().length > DESTINATION_TEXT_MAX_LEN) {
    errors['destinationText'] =
      `Destination must be ${String(DESTINATION_TEXT_MAX_LEN)} characters or fewer.`;
  } else {
    // ⛔ CLEANED BEFORE IT IS PARSED, AND THE ORDER IS THE POINT (security review, board 2190).
    // This field is read by a DRIVER, and `cleanStoredText` exists because a bidi control once
    // survived every caller, landed in `portal.homes.fallback_phone` and rendered the number
    // REVERSED on the screen Helen rings from (measured 2026-09-08, boards 1868/1873). The
    // sibling route already cleans `displayName` this way (`server/request-routes.ts`).
    // ⚠️ What this row ADDS to that standing hazard is a format CONTRACT: a reader is now taught
    // that the value leads with an Eircode, so a U+202E in the hint could make the rendered line
    // disagree with the stored one. Cleaning first is what keeps the contract true on screen.
    // It returns null when nothing readable survives, which lands on the same refusal below.
    const cleaned = cleanStoredText(rawDestinationText);
    const parsed = cleaned === null ? null : parseDestinationEircode(cleaned);
    if (parsed === null) {
      errors['destinationText'] = DESTINATION_EIRCODE_MESSAGE;
    } else {
      const stored = formatDestination(parsed);
      if (stored.length > DESTINATION_TEXT_MAX_LEN) {
        errors['destinationText'] =
          `Destination must be ${String(DESTINATION_TEXT_MAX_LEN)} characters or fewer.`;
      } else {
        destinationText = stored;
      }
    }
  }

  // returnTrip (T1683 M7): does the job come back?
  //
  // ⛔ ABSENT/NULL IS `false`, A NON-BOOLEAN IS AN ERROR. Absent is the honest
  // answer for every caller that predates this field and for every request
  // already in the table — the column is NOT NULL DEFAULT false because a
  // one-way journey really is one-way, not a placeholder. But a string "true",
  // or a 1, is a caller that MEANT something and got the type wrong, and
  // coercing it here would either invent a doubled price or silently drop the
  // nurse's answer. Refused by name instead.
  //
  // ⭐ A SECOND FIELD JOINS IT IN T1717 M5, AND THAT IS THE REVERSAL OF THIS
  // PARAGRAPH'S OWN RULING. `return_at` is no longer deferred: Alex reversed
  // board #1681's deferral on 2026-09-02 via `/scope` of the QA report
  // (question 708 in the lane store), and M5 ADDED the validator arm below this
  // one - `returnAt` required when `returnTrip` is ticked on a ONE-OFF request,
  // compared as an INSTANT against `journeyDate`, and backed by the v11 CHECK
  // constraints `requests_return_at_needs_return_trip` and
  // `requests_return_after_outbound`.
  // ⛔ The boolean's own by-name refusal below is UNCHANGED by that: this field
  // still refuses a string "true" or a 1 rather than coercing it.
  //
  // ⚠️ PLAIN BRACKET ACCESS, NOT `Object.hasOwn` — RAISED AT THE M7 REVIEW (G)
  // AND DELIBERATELY LEFT (it is the kiosk's crossing handler that guards this
  // class with `Object.hasOwn`, e.g. `server/app.ts`'s `own()` accessor). Two
  // reasons, and the second is the one that decides it:
  //   · NOT REACHABLE TODAY. `portal/src` has no merge/spread sink into a body,
  //     and `JSON.parse` makes `__proto__` an OWN property rather than walking
  //     the prototype — measured, not assumed.
  //   · EVERY OTHER READ IN THIS VALIDATOR IS THE SAME PLAIN BRACKET FORM
  //     (`payerKind`, `timeMeaning`, `destinationId`, …). Hardening THIS ONE
  //     field would leave the guard looking complete while eleven siblings stay
  //     open — a worse state than uniform-and-known.
  // ⇒ TRIGGER: the day `portal/src` gains a merge sink into a request body, or a
  // non-JSON body parser lands, convert the WHOLE function to a `hasOwn`
  // accessor in one change. Never just this field.
  let returnTrip = false;
  const rawReturnTrip = body['returnTrip'];
  if (rawReturnTrip === undefined || rawReturnTrip === null) {
    returnTrip = false;
  } else if (typeof rawReturnTrip !== 'boolean') {
    errors['returnTrip'] = 'Say whether the taxi brings them back.';
  } else {
    returnTrip = rawReturnTrip;
  }

  // payerKind
  let payerKind: PayerKind = 'hse';
  const rawPayer = body['payerKind'];
  const knownPayers: readonly string[] = PAYER_KINDS;
  if (typeof rawPayer === 'string' && knownPayers.includes(rawPayer)) {
    payerKind = rawPayer as PayerKind;
    // Board 2186: the offered set depends on the destination's CLASS, and the server decides it
    // from the STORED destination fields rather than from anything the client asserts. This runs
    // AFTER the destination XOR above, so `destinationId` / `destinationText` are already resolved.
    //
    // ⛔ SKIP WHEN THE DESTINATION ITSELF IS ALREADY WRONG. One bad destination must raise ONE
    // error: without this guard a body with both fields (or neither) reports a payer error too,
    // and the form would focus a control the nurse has no way to correct.
    const destinationDecided =
      errors['destinationId'] === undefined && errors['destinationText'] === undefined;
    if (destinationDecided && !allowedPayerKinds(destinationText !== null).includes(payerKind)) {
      errors['payerKind'] = 'This destination is billed to the home account.';
    }
  } else {
    errors['payerKind'] = 'Choose who is paying.';
  }

  // ⛔ THE TWO NEXT-OF-KIN BLOCKS STOOD HERE (T1868 M6). Removing them removes
  // the ONLY validation this function performed on third-party contact data,
  // and the ONLY caller of `cleanStoredText` inside it — so `room`, `notes` and
  // `destinationText` are, and always were, length-checked but NOT cleaned here.
  // ⚠️ That is a pre-existing property this milestone neither introduced nor
  // fixed; it is recorded so the absence of a cleaner below is not read as
  // something M6 took away.

  // journeyDate / schedulePattern: exactly one of the two, XOR.
  // ⭐ Board 2638: `''` is absent, as null is. The day arrives from an
  // `<input type="date">`, whose blank is the empty string.
  const rawJourneyDate = body['journeyDate'];
  const rawSchedulePattern = body['schedulePattern'];
  const hasJourneyDate = isPresent(rawJourneyDate);
  const hasSchedulePattern = rawSchedulePattern !== null && rawSchedulePattern !== undefined;

  let journeyDate: string | null = null;
  // ⭐ Board 2638 — the Irish DAY, kept whenever it is a real calendar day, even
  // when the outbound then fails its time or a clock bound: the return composes
  // on it, so she hears about a bad return time in the same round.
  let journeyDay: string | null = null;
  let schedulePattern: SchedulePattern | null = null;

  // ⛔⛔ T1868 M2 — RECURRENCE IS REFUSED HERE, AND IT MUST COME **BEFORE** THE XOR.
  //
  // LCC's request is a form offering One way or Return and nothing else, so the
  // browser can no longer construct a pattern. That makes this refusal an API
  // guard: a DIRECT POST still can, and the seam has to say no BY NAME rather
  // than let a pattern through to a system that has stopped rendering one.
  //
  // ⛔ WHY THE ORDER IS LOAD-BEARING, and why deleting the recurring branch
  // instead would have been wrong. The XOR below fires on
  // `hasJourneyDate === hasSchedulePattern`, i.e. on "neither" as well as "both".
  // A caller posting ONLY a pattern would, with the recurring branch simply
  // removed, fall through to that test with `hasJourneyDate` false and
  // `hasSchedulePattern` true — unequal — and so slip PAST the XOR into the code
  // below, which reads `journeyDate` as the trip instant and would find none.
  // Refusing first is what makes the rest of this function able to assume there
  // is always a journey date.
  //
  // ⛔ FIELD-KEYED, not a route-level `{error: code}`. The client renders
  // `body.errors` and nothing else, so a code at the route would not reach a
  // screen at all. This key is the validator's native shape.
  // ⚠️ AFTER M2 THIS ERROR IS API-ONLY AND STRUCTURALLY UNRENDERABLE, WHICH IS
  // CORRECT — the recurring fieldset that used to display it is deleted, along
  // with its `ERROR_FIELD_IDS` focus entry. No nurse can reach it, because no
  // nurse can construct a pattern. It guards the wire, not the screen.
  //
  // ⛔ THE MESSAGE NAMES THE TWO TRIP TYPES AND NOTHING ELSE — no id, no SQL, no
  // enumeration of the shapes that used to be accepted, which would tell a
  // caller what to try next.
  if (hasSchedulePattern) {
    errors['schedulePattern'] = 'Repeating trips are not booked through this form. Ask for One way or Return, one journey at a time.';
    return { ok: false, errors };
  }

  if (hasJourneyDate === hasSchedulePattern) {
    // T632: with no day, a blank or malformed time still gets its OWN error
    // rather than this one, so she is told about the control she can see.
    // ⚠️ Enforced only when the `journeyTime` key is PRESENT, HEAD's rule kept
    // byte for byte (board 2638): a body with neither key hears only
    // "Enter the date of the journey.". Since 2638 the day and the time always
    // travel separately, and the time is required once there is a day (below).
    const rawJourneyTime = body['journeyTime'];
    if (rawJourneyTime !== undefined && !hasSchedulePattern) {
      if (typeof rawJourneyTime !== 'string' || !TIME_RE.test(rawJourneyTime)) {
        errors['journeyTime'] = 'Enter the time in 24-hour format, like 09:30.';
      }
    }
    if (errors['journeyTime'] === undefined) {
      // Both present or both absent: neither is a valid request.
      // ⛔⛔ T1868 M2 (review) — THIS SENTENCE WAS FALSE IN EVERY REACHABLE CASE.
      // It read "Choose either a one-off date or a recurring pattern, not both."
      // The XOR fires on `hasJourneyDate === hasSchedulePattern`, i.e. on NEITHER
      // as well as BOTH — and the recurrence refusal above now returns before a
      // pattern can ever reach here, so the "both" case is unreachable and the
      // only way in is "neither". So it told a nurse not to do something she had
      // not done, and named a control this milestone deleted. It is the same
      // defect class M2 repaired for `returnAt` and `returnTripHint`, and it was
      // missed because the code around it changed while the STRING did not.
      // ⇒ The key can now mean exactly one thing, which is also why it finally
      // has an honest `ERROR_FIELD_IDS` target (`rf-date`).
      errors['journey'] = 'Enter the date of the journey.';
    }
  } else if (hasJourneyDate) {
    // ⭐⭐ Board 2638 (plan D1) — THE WIRE CARRIES WHAT SHE TYPED: the Irish day
    // here and the Irish `HH:MM` in `journeyTime`, composed in `Europe/Dublin` by
    // `dublinInstant`, never in the device's zone. The canonical ISO instant is
    // what is stored, as before. ⚠️ The T632 arm that refused a bare day is
    // folded in: a bare day IS the shape now, and a blank time beside it is
    // still refused by name below, so no midnight nobody stated can be stored.
    const rawJourneyTime = body['journeyTime'];
    if (typeof rawJourneyDate !== 'string' || dublinInstant(rawJourneyDate, '00:00') === null) {
      // Not a string, not `YYYY-MM-DD`, or not a calendar day (`2026-09-31`).
      errors['journeyDate'] = 'Journey date must be a valid date.';
    } else {
      journeyDay = rawJourneyDate;
      const iso = typeof rawJourneyTime === 'string' ? dublinInstant(rawJourneyDate, rawJourneyTime) : null;
      if (iso === null) {
        errors['journeyTime'] = 'Enter the time in 24-hour format, like 09:30.';
      } else {
        // ⭐ Board 2788: the two clock bounds live in `journeyBoundError`, shared
        // with `deriveJourneyChange`; the messages are unchanged.
        const bound = journeyBoundError(iso, opts.now, lowerBoundsOnly);
        if (bound !== null) {
          errors['journeyDate'] = bound.error;
        } else {
          journeyDate = iso;
        }
      }
    }
  }
  // ⛔⛔ T1868 M2 (review) — THE PATTERN-PARSE BRANCH IS DELETED, AND IT WAS
  // UNREACHABLE RATHER THAN MERELY UNUSED. ~55 lines parsed `frequency`,
  // `weekday`, `intervalWeeks`, `dayOfMonth` and `time` into a `SchedulePattern`,
  // with granular messages ("Weekday must be between 0 and 6", "Repeat every 2 to
  // 12 weeks"). The recurrence refusal at the top of this function RETURNS on
  // `hasSchedulePattern`, so nothing could ever arrive here with a pattern.
  //
  // ⚠️ WHY IT SURVIVED THE FIRST PASS, WHICH IS THE REUSABLE PART: `tsc` cannot
  // see it — reachability across a DERIVED BOOLEAN is a value-level fact, not a
  // type-level one — and the milestone's own "refuses a MALFORMED pattern too"
  // arm passed WITHOUT it, because every junk shape it feeds in stops at the
  // blanket refusal and never reaches a granular message. A passing test was
  // quietly certifying dead code. Three reviewers found it independently, one by
  // mutating the block to `} else if (false) {` and watching the ENTIRE portal
  // suite stay green.
  //
  // ⛔ `SchedulePattern` THE TYPE STAYS, and that is not an oversight: stored
  // rows written before M2 still carry patterns, and `format.ts`, both alert
  // composers and the `/ops` table all READ them. What is deleted is the code
  // that could only ever have parsed an INBOUND one.

  // returnAt (T1717 M5, DD4): WHEN the bus brings them back.
  //
  // ⛔ TWO STATES THAT MATTER, AND THE TICK DECIDES WHICH. Not ticked → no return
  // is composed, whatever `returnTime` says: the tick is the answer, and a time
  // left in the control after an untick is not. Ticked on a ONE-OFF request →
  // REQUIRED (a time, or the to-confirm marker), and compared as an instant
  // against the outbound. There is no recurring arm: recurrence is refused above.
  //
  // ⛔ THE MODE IS READ FROM `hasJourneyDate`, NOT FROM THE PARSED LOCALS. A
  // one-off request whose date failed its own validation must still be told its
  // return is missing, or she fixes the date, submits again and meets a second
  // error she could have been shown the first time.
  //
  // ⭐⭐ Board 2638 (plan D1) — THE VALIDATOR COMPOSES THE RETURN. The wire
  // carries the typed `returnTime` and the instant is `dublinInstant(journey
  // day, returnTime)`, the same pinned-zone composer as the outbound, so both
  // legs are Irish time and comparable. Until 2638 the BROWSER composed it in
  // the device's zone (`combineDateAndTime`) and posted `returnAt`; that key is
  // now a stale-page fingerprint, refused at the top of this function, so the
  // "Choose Return before saying when they come back." refusal that stood in
  // the unticked arm is deleted: a `returnAt` value can no longer reach it.
  // ⚠️ SAME-DAY IS THE FORM'S RULE (2026-09-13, Alex, board 2073), and since
  // 2638 the wire's too: the return composes on the journey's own day, so a
  // later-day return cannot be posted. A multi-day return is by phone.
  const rawReturnTime = body['returnTime'];
  const hasParseableReturnTime = typeof rawReturnTime === 'string' && TIME_RE.test(rawReturnTime);
  let returnAt: string | null = null;
  // ⭐⭐ T2088 M2 (ADR-0006, plan D2) — THE MARKER, READ BY PRESENCE AND THEN
  // STRICTLY. `Object.hasOwn`, NOT the plain bracket read every sibling field
  // takes, and the `returnTrip` paragraph above ("Never just this field") is
  // not contradicted: that ruling is about VALUE fields, where a missing key and
  // an `undefined` value mean the same thing and a prototype walk could only
  // ever supply a value the code then type-checks. This key is a PRESENCE
  // MARKER whose presence IS the answer, so the check a polluted
  // `Object.prototype.returnAtState = 'to-confirm'` would satisfy is exactly the
  // one the request never made. The house idiom is the kiosk's `own()` accessor
  // (`quote-helper/src/server/app.ts`); the fourth route-level arm in
  // `test/server/request-routes.spec.ts` pollutes the prototype and grades it.
  const hasReturnAtStateKey = Object.hasOwn(body, 'returnAtState');
  const toConfirm = hasReturnAtStateKey && body['returnAtState'] === 'to-confirm';
  let returnAtState: NewRequest['returnAtState'];
  if (!returnTrip) {
    // ⛔ T2088 M2 — A "time to be confirmed" on a job that does not come back is
    // a contradiction, and v14's `requests_to_confirm_needs_timeless_return`
    // would refuse the row as a bare 23514 if it got past here. Presence of the
    // key is enough: there is no value of it that means anything on a one-way job.
    if (hasReturnAtStateKey) {
      errors['returnAtState'] = 'Choose Return before saying the return time is to be confirmed.';
    }
  } else if (hasJourneyDate) {
    // ⭐⭐ T2088 M2 (ADR-0006, plan D2) — THE SHAPE, one arm each:
    //   · marker PRESENT but not exactly `'to-confirm'` → refused, keyed
    //     `returnAtState`. A closed vocabulary with one legal wire value.
    //   · marker `'to-confirm'` AND a parseable `returnTime` → refused, keyed
    //     `returnAtState`. The time she gave is never silently dropped; she
    //     sends it or she says it is to be confirmed.
    //   · marker `'to-confirm'` alone → accepted; `returnAt` stays null and the
    //     value carries the state. The repo writes it, v14's two CHECKs back it.
    //   · marker OMITTED and no parseable time → refused, keyed `returnTime`.
    //     An omission is not an answer (code-review WARN, 2026-09-13).
    //   · otherwise → composed on the journey's day and bounded.
    if (hasReturnAtStateKey && !toConfirm) {
      errors['returnAtState'] = 'Say the return time, or say it is to be confirmed.';
    } else if (toConfirm) {
      if (hasParseableReturnTime) {
        errors['returnAtState'] =
          'You gave a return time and also said it is to be confirmed. We have not dropped the time. Send the time, or clear it and say it is to be confirmed.';
      } else {
        returnAtState = 'to-confirm';
      }
    } else if (!hasParseableReturnTime) {
      // ⭐⭐ A2-newrequest-2 (T1723 M4) — THE RETURN TIME REFUSES IN ITS OWN
      // WORDS, with its own example hour (14:30), because both time fields are on
      // screen at once when Return is chosen and one sentence heard twice names
      // neither control.
      errors['returnTime'] = 'Enter the return time in 24-hour format, like 14:30.';
    } else if (journeyDay === null) {
      // The day itself failed (refused above), so there is no day to compose the
      // return on. HEAD's sentence for "a well-formed time with no instant".
      errors['returnTime'] = 'We could not work out the return time. Try again, or ring East Coast Minibus.';
    } else {
      // ⛔ THE THREE INSTANT CHECKS LIVE IN `validateReturnInstant` (T2088 M5),
      // shared with the edit route, so the window cannot drift. `journeyDate` is
      // null exactly when the outbound failed its own checks, and passing that
      // null on is what keeps a nurse from holding two refusals for one mistake.
      // ⚠️ M1 security review (Med, 2026-09-13) found a well-formed `returnTime`
      // with no instant stored `return_at = NULL` on a ticked return; since 2638 a
      // parseable time always composes here or is refused, so that cannot recur.
      const checked = validateReturnInstant(
        dublinInstant(journeyDay, rawReturnTime as string),
        journeyDate,
        lowerBoundsOnly ? null : opts.now,
      );
      if (!checked.ok) {
        errors['returnAt'] = checked.error;
      } else {
        returnAt = checked.iso;
      }
    }
  }

  // timeMeaning (T632): what the stated time refers to.
  //
  // ⛔⛔ T1868 M2 (review) — THIS BLOCK DESCRIBED A RULE THIS MILESTONE DELETED,
  // and it survived the wave-2 sweep that removed the code beneath it, which is
  // the same rot class that sweep existed to close. Recorded rather than quietly
  // rewritten, because the retired reasoning is the part worth keeping.
  //
  // ⚠️ IT SAID: "REQUIRED on the recurring branch, OPTIONAL on the one-off
  // branch", called that asymmetry deliberate, and explained it — the recurring
  // branch was the pilot's lead shape (a weekly dialysis run) and was exercised by
  // no Playwright journey, while the one-off branch was driven by a serial `e2e/`
  // spec "that this lane does not own and that never sets a radio", so requiring
  // it there would have red a lane this lane could not repair. It also said the
  // requirement was "Gated on schedulePattern having PARSED, not merely being
  // present, so a malformed pattern reports its own error alone".
  //
  // ⛔ ALL OF THAT IS NOW FALSE. There is no recurring branch, nothing parses a
  // pattern, and **T1868 owns `e2e/tests/portal/**`** — the constraint that made
  // the asymmetry necessary no longer applies.
  // ⚠️ ⇒ `timeMeaning` is now OPTIONAL ON EVERY NEW REQUEST, which is a live gap,
  // not a resolution: a nurse can leave it unstated and Helen reads a bare time
  // with no meaning. **M5 step 2 closes it** (plan risk R10, High/High) by making
  // the key required on the surviving branch, with its own `ERROR_FIELD_IDS`
  // entry — and M5 must update the three `e2e/` sites in the same commit, which
  // it can, because this lane owns them.
  let timeMeaning: TimeMeaning | null = null;
  const rawTimeMeaning = body['timeMeaning'];
  const knownTimeMeanings: readonly string[] = TIME_MEANINGS;
  if (rawTimeMeaning === undefined || rawTimeMeaning === null || rawTimeMeaning === '') {
    // ⛔⛔ T1868 M5 — **REQUIRED**, AND THE GAP M2 NAMED IS CLOSED HERE. M2 left
    // this arm empty and said so: after it there was no recurring branch, so the
    // key was optional on EVERY new request, a nurse could skip the radio, and
    // Helen read a bare time labelled "not stated" — precisely the LCC complaint
    // P0-4 exists to remove, surviving inside the milestone that claims to fix
    // it.
    // ⛔ AND IT IS NOW LOAD-BEARING TWICE OVER, not merely tidy: `pickupAt`
    // below derives ONLY on the `appointment` arm, so an unstated meaning would
    // mean no derivation at all — a NULL pickup on a request whose whole purpose
    // was to carry one. Requiring the answer invents nothing (the form still
    // pre-selects no radio, `request-form-logic.ts`'s own rule) and guarantees
    // the derivation runs.
    // ⚠️ THE HISTORICAL REASON FOR THE ASYMMETRY NO LONGER APPLIES, and it was
    // the only reason: the one-off branch was "driven end-to-end by a serial
    // spec in `e2e/` that this lane does not own", so requiring it there would
    // have red a lane that could not be repaired from here. **T1868 owns
    // `e2e/tests/portal/**`**, and the three sites are updated in this same
    // commit.
    errors['timeMeaning'] = 'Say what this time refers to.';
  } else if (typeof rawTimeMeaning !== 'string' || !knownTimeMeanings.includes(rawTimeMeaning)) {
    errors['timeMeaning'] = 'Say what this time refers to.';
  } else {
    timeMeaning = rawTimeMeaning as TimeMeaning;
  }

  // pickupAt (T1868 M5, P0-4): WHEN THE BUS COLLECTS HER.
  //
  // ⛔ AFTER `timeMeaning`, AND THAT ORDER IS THE RULE ITSELF. Whether a pickup
  // is derived at all is decided by the meaning, so this block cannot run before
  // the meaning has parsed — the same dependency `returnAt` has on `returnTrip`.
  //
  // ⭐⭐ Board 2638 (plan D1) — DERIVED HERE, never read off the wire: the
  // appointment instant minus the session home's `pickup_offset_minutes`
  // (`opts.pickupOffsetMinutes`, read by the route), through `pickupInstantFor`,
  // the function the form's Collection preview runs too. Until 2638 the browser
  // derived it and posted `pickupAt`, and this block only bounded that
  // client-supplied value (the gap security review M1, board 2188, named).
  // ⇒ The `collection` arm's by-name refusal ("A collection time is already the
  // pickup, so there is no second one.") is deleted: any `pickupAt` value is now
  // refused as a stale page first, so it could not be reached.
  //
  // ⛔ GATED ON A VALID OUTBOUND INSTANT. A blank, malformed or out-of-bounds
  // appointment is refused above, and deriving from it would put a second error
  // under the same control for one mistake. Every error here renders under the
  // appointment's Time control, the one thing a nurse can change.
  let pickupAt: string | null = null;
  // ⭐ Board 2647 (P4): the ONE offset read, used for the derivation and
  // recorded beside it, so the row's two columns cannot disagree.
  let pickupOffsetMinutesUsed: number | null = null;
  if (timeMeaning === 'appointment' && journeyDate !== null) {
    // ⭐ Board 2788: the five-bound chain lives in `derivePickupChecked`, shared
    // with `deriveJourneyChange`; its reasoning moved with it, the messages are
    // unchanged.
    const pickup = derivePickupChecked(journeyDate, opts.pickupOffsetMinutes ?? null, opts.now, lowerBoundsOnly);
    if (!pickup.ok) {
      errors['pickupAt'] = pickup.error;
    } else {
      pickupAt = pickup.pickupAt;
      pickupOffsetMinutesUsed = pickup.offsetUsed;
    }
  }

  // notes (optional; defaults to '')
  let notes = '';
  const rawNotes = body['notes'];
  if (rawNotes === undefined || rawNotes === null) {
    notes = '';
  } else if (typeof rawNotes !== 'string') {
    errors['notes'] = 'Notes must be text.';
  } else if (rawNotes.length > NOTES_MAX_LEN) {
    errors['notes'] = `Notes must be ${String(NOTES_MAX_LEN)} characters or fewer.`;
  } else {
    notes = rawNotes;
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    value: {
      residentId,
      requestedByStaffId,
      room,
      equipment,
      destinationId,
      destinationText,
      returnTrip,
      returnAt,
      // ⭐ T2088 M2 — ON THE VALUE ONLY WHEN IT WAS SAID. The key is optional on
      // the type and absent means "unset" at the repo, so an always-present
      // `undefined` would put a key on the value that the wire never carried
      // and that every KEY-SET pin in this package would then have to explain.
      ...(returnAtState === undefined ? {} : { returnAtState }),
      payerKind,
      journeyDate,
      schedulePattern,
      timeMeaning,
      pickupAt,
      pickupOffsetMinutesUsed,
      notes,
    },
  };
}
